'use client';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Button from 'components/Button';
import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import Entering from './Entering';
import Typography from './Typography';

interface ISliderScroller {
  cards?: {
    title: string;
    icon?: ReactNode;
    description?: string;
  }[];
  fontSize?: number;
  alignCenter?: boolean;
  centralize?: boolean;
}

const SliderScroller: FC<PropsWithChildren<ISliderScroller>> = ({
  cards = [],
  fontSize = 24,
  centralize = true,
  alignCenter
}) => {
  const [percentage, setPercentage] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(
    () =>
      setPercentage(
        ref.current
          ? (ref.current?.scrollLeft / (ref.current?.scrollWidth - ref.current?.clientWidth)) * 100
          : 0
      ),
    [ref]
  );

  const handleLeftArrow = useCallback(() => {
    if (ref.current)
      ref.current.scrollTo({ left: ref.current.scrollLeft - 352, behavior: 'smooth' });
  }, [ref]);

  const handleRightArrow = useCallback(() => {
    if (ref.current)
      ref.current.scrollTo({ left: ref.current.scrollLeft + 352, behavior: 'smooth' });
  }, [ref]);

  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (ref.current) {
      if (e.key === 'ArrowLeft') return (ref.current.scrollLeft -= 352); // Adjust the scrollLeft as needed

      if (e.key === 'ArrowRight') return (ref.current.scrollLeft += 352); // Adjust the scrollLeft as needed

      return;
    }
  };

  useEffect(() => {
    const slider = ref.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);

      if (typeof window !== 'undefined') window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (slider) slider.removeEventListener('scroll', handleScroll);
    };
  }, [centralize, handleScroll, ref]);

  useEffect(() => {
    if (ref.current && centralize) ref.current.scrollTo({ left: 171, behavior: 'smooth' });
  }, [centralize, ref]);

  return (
    <div className="relative z-0">
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-0 z-50 size-full transition-all ease-in-out'
        )}
        style={{
          boxShadow: [
            ...(percentage < 5 ? [] : ['rgb(249,250,251,0.6) 1rem 0px 5rem 0px inset']),
            ...(percentage > 97 ? [] : ['rgb(249,250,251,0.6) -1rem 0px 5rem 0px inset'])
          ].join(', ')
        }}
      />
      <div className="flex flex-col justify-between">
        <div
          className={cn(
            'mx-auto my-0 grid max-w-[352px] grid-flow-col gap-4 p-4',
            'snap-x snap-mandatory overflow-y-hidden overscroll-x-contain scroll-smooth scrollbar-none',
            'sm:w-full sm:max-w-full sm:gap-8'
          )}
          ref={ref}
        >
          {cards?.map(({ title, icon, description }, index) => (
            <Entering
              damping={20}
              mass={0.5}
              stiffness={120}
              delay={index * 0.2}
              key={title}
              className={cn(
                'w-[352px] max-w-none snap-center snap-always  rounded-6xl',
                'border border-tolq-gray-100 bg-white p-6',
                'sm:max-w-full sm:snap-start sm:scroll-ml-[176px]'
              )}
            >
              <div className="rounded-3xl bg-tolq-gray-200">{icon}</div>
              <div className="py-3">
                <Typography
                  variant="bold4xl"
                  className="text-center text-tolq-black-100"
                  style={{ fontSize }}
                >
                  {alignCenter ? <div className="text-center ">{title}</div> : <>{title}</>}
                </Typography>
              </div>
              <div>
                <Typography variant="light3" className="text-tolq-black-200">
                  {alignCenter ? (
                    <div className="text-center ">{description}</div>
                  ) : (
                    <>{description}</>
                  )}
                </Typography>
              </div>
            </Entering>
          ))}
        </div>
        <div className="flex w-full justify-center">
          <div className="flex gap-16 py-6">
            <Button
              isRipple
              variant="text"
              colorIcon="black"
              className="!m-0 size-11 !rounded-full !bg-white !p-0 shadow-custom-dark"
              startAdornment={<ChevronLeftIcon className="size-8" />}
              onClick={handleLeftArrow}
            />
            <Button
              isRipple
              variant="text"
              colorIcon="black"
              className="!m-0 size-11 !rounded-full !bg-white !p-0 shadow-custom-dark"
              startAdornment={<ChevronRightIcon className="size-8" />}
              onClick={handleRightArrow}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SliderScroller;
